import React, { useMemo, useState } from 'react';
import { Star, Quote } from 'lucide-react';

interface TestimonialCardProps {
  name: string;
  location: string;
  content: string;
  rating: number;
  service: string;
  image?: string;
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({
  name,
  location,
  content,
  rating,
  service,
  image
}) => {
  const [expanded, setExpanded] = useState(false);
  const previewText = useMemo(() => {
    if (expanded) return content;
    const MAX = 280;
    if (!content || content.length <= MAX) return content;
    const slice = content.slice(0, MAX);
    const lastBreak = Math.max(slice.lastIndexOf('\n'), slice.lastIndexOf(' '));
    const safeSlice = lastBreak > 0 ? slice.slice(0, lastBreak) : slice;
    return `${safeSlice.trim()}…`;
  }, [content, expanded]);

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
      <div className="flex items-center mb-4">
        <div className="w-12 h-12 bg-gradient-to-br from-accent-400 to-accent-600 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
          {image ? (
            <img 
              src={image} 
              loading="lazy"
              alt={`${name} - ${service} testimonial`} 
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            name.charAt(0).toUpperCase()
          )}
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900">{name}</h4>
          <p className="text-sm text-gray-600">{location}</p>
          <div className="flex items-center mt-1">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-4 h-4 ${
                  i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
      
      <div className="relative mb-3">
        <Quote className="w-6 h-6 text-accent-200 absolute -top-2 -left-1" />
        <p className="text-gray-700 leading-relaxed pl-4 whitespace-pre-line">{previewText}</p>
      </div>
      <div className="pl-4 mb-4">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-accent-600 hover:text-accent-700 text-sm font-medium"
          aria-expanded={expanded}
        >
          {expanded ? 'Read less' : 'Read more'}
        </button>
      </div>
      
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-accent-600 bg-accent-50 px-3 py-1 rounded-full">
          {service}
        </span>
        <div className="flex items-center text-sm text-gray-500">
          <span>Verified Client</span>
        </div>
      </div>
    </div>
  );
};

export default TestimonialCard;
